import { useEffect, useMemo } from 'react'
import { trackEvent } from '../../../utils/appBridge'
import { EventNames } from '../../../types/misc.types'
import { InvestPaymentMethodType, InvestPaymentMethodTypes } from '../../../types/mf-execution.types'
import { formatIndianNumber } from '../../../utils/FD/fd'
import { RUPEE_SYMBOL } from '../../../constants'
import RenderHTML from '../../ui-components/RenderHTML'
import { investPaymentMethodConfig } from '../../../utils/mf-execution/paymentPageConfig'
import BottomSheetContent from '../BottomSheetContent'
import { convertNumberToCurrency } from '../../../utils/utils'
import { AddPaymentMethod } from '../../execution/AddPaymentMethod'

type PaymentMethodsBottomSheetProps = {
  onDismiss: () => void
  ScreenName: string
  paymentMethods: InvestPaymentMethodType[]
  amount: number
  selectedPaymentMethod: InvestPaymentMethodType | null
  handlePaymentMethodChange: (paymentMethod: InvestPaymentMethodType | null) => void
  onAddUpiClick: () => void
  isPaymentDisabled: boolean
  trackingFund?: { fundIsin: string; fundName: string }
}

export const InvestPaymentMethodsBottomSheet = ({
  onDismiss,
  ScreenName,
  paymentMethods,
  amount,
  selectedPaymentMethod,
  handlePaymentMethodChange,
  onAddUpiClick,
  isPaymentDisabled,
  trackingFund
}: PaymentMethodsBottomSheetProps) => {
  const heading = 'Select payment method'

  useEffect(() => {
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        bottomSheetName: 'Payment method selection',
        ...(trackingFund ?? {})
      }
    })
  }, [])

  const isAmountGreaterThanMaxAllowedAmount = (paymentMethod: InvestPaymentMethodType) => {
    if (paymentMethod?.maximumAllowedAmount) {
      return amount > paymentMethod?.maximumAllowedAmount
    }
    return false
  }

  const isAddUpiDisabled = useMemo(() => !(amount > 0), [amount])

  const getAddUpiCaption = () => {
    if (isAddUpiDisabled) {
      return 'Enter amount to continue'
    } else if (isPaymentDisabled) {
      return 'Please enter a valid amount'
    }
    return undefined
  }

  return (
    <BottomSheetContent bottomSheetBodyClassName='!px-0' onDismiss={onDismiss}>
      <div test-locator='MFE_BS_SELECT_PAYMENT_METHOD_BOTTOM_SHEET' className='flex flex-col'>
        <div className='flex flex-col px-5 pb-2'>
          <div className='text-[18px] font-[500] text-[#232323]'>{heading}</div>
          {amount > 0 && (
            <div className='text-[#6A6E6E] text-[13px] font-[400] pt-1'>
              For {RUPEE_SYMBOL}
              {formatIndianNumber(amount)}
            </div>
          )}
        </div>
        <div className='flex flex-col'>
          {paymentMethods?.map((paymentMethod, idx) => {
            const paymentConfig = investPaymentMethodConfig?.[paymentMethod?.type] ?? {}
            return (
              <div
                key={`${paymentMethod?.type}-${idx}`}
                onClick={() => {
                  if (isAmountGreaterThanMaxAllowedAmount(paymentMethod)) {
                    return
                  }
                  trackEvent({
                    eventName: EventNames.UserClicked,
                    payload: {
                      ScreenName,
                      eventId: 'MFE_BS_PAYMENT_METHOD_SELECT',
                      buttonLabel:
                        paymentMethod?.type === InvestPaymentMethodTypes.UPI
                          ? paymentMethod?.upiId || 'UPI'
                          : paymentConfig?.label || paymentMethod?.type || 'Payment method',
                      ...(trackingFund ?? {})
                    }
                  })
                  handlePaymentMethodChange(paymentMethod)
                  onDismiss()
                }}
                className='flex flex-col px-5 py-5 w-full border-b border-[#F0F4F7]'
              >
                <div
                  className={`flex items-center justify-between w-full ${isAmountGreaterThanMaxAllowedAmount(paymentMethod) ? 'opacity-50' : ''
                    }`}
                >
                  <div className='flex items-center w-1 flex-1'>
                    <div className='flex items-center mr-3'>
                      <RenderHTML innerHTMLText={paymentConfig?.icon} />
                    </div>
                    <div className='flex flex-col w-1 flex-1'>
                      <span
                        className={`font-[500] text-[13px] whitespace-nowrap overflow-hidden text-ellipsis max-w-full ${isAmountGreaterThanMaxAllowedAmount(paymentMethod) ? 'text-[#8E9393]' : 'text-[#232323]'
                          }`}
                      >
                        {paymentMethod?.type === InvestPaymentMethodTypes.UPI
                          ? paymentMethod?.upiId || ''
                          : paymentConfig?.label || ''}
                      </span>

                      <div
                        className={`font-[400] text-[13px] pt-[2px] truncate max-w-[90%] ${isAmountGreaterThanMaxAllowedAmount(paymentMethod) ? 'text-[#8E9393]' : 'text-[#6A6E6E]'
                          }`}
                      >
                        {paymentMethod?.displayAccNumber}
                      </div>
                    </div>
                  </div>
                  <div>
                    <RenderHTML
                      innerHTMLText={
                        selectedPaymentMethod &&
                          paymentMethod &&
                          JSON?.stringify(selectedPaymentMethod) === JSON?.stringify(paymentMethod)
                          ? CheckedIcon
                          : UnCheckedIcon
                      }
                    />
                  </div>
                </div>
                {(() => {
                  if (isAmountGreaterThanMaxAllowedAmount(paymentMethod)) {
                    return (
                      <div
                        style={{ background: 'linear-gradient(90deg, #F0F4F7 0%, rgba(240, 244, 247, 0.00) 100%)' }}
                        className='text-[#6A6E6E] font-[400] text-[13px] rounded-[8px] px-[8px] py-[4px] pr-10 ml-[52px] mt-3 w-max'
                      >
                        Maximum {convertNumberToCurrency({ amount: paymentMethod?.maximumAllowedAmount ?? 0 })} allowed
                      </div>
                    )
                  } else if (
                    paymentMethod?.type === InvestPaymentMethodTypes.MANDATE &&
                    JSON?.stringify(selectedPaymentMethod) === JSON?.stringify(paymentMethod)
                  ) {
                    return (
                      <div
                        style={{ background: 'linear-gradient(90deg, #F0F4F7 0%, rgba(240, 244, 247, 0.00) 100%)' }}
                        className='text-[#6A6E6E] font-[400] text-[13px] rounded-[8px] px-[8px] py-[4px] pr-10 ml-[52px] mt-3 w-max'
                      >
                        Auto debited in 1–2 working days
                      </div>
                    )
                  }
                  return null
                })()}
              </div>
            )
          })}
          {/* <div className='px-5 py-5 w-full border-b border-[#F0F4F7]'>
            <AddPaymentMethod addType='bank' isDisabled={true} />
          </div> */}
          <div className='px-5 py-5 w-full'>
            <AddPaymentMethod
              onAdd={() => {
                trackEvent({
                  eventName: EventNames.ButtonTap,
                  payload: {
                    ScreenName,
                    eventId: 'MFE_BS_SELECT_PAYMENT_METHOD_CTA_ADD_UPI',
                    buttonLabel: 'Add UPI ID',
                    ...(trackingFund ?? {})
                  }
                })
                onAddUpiClick()
              }}
              addType='upi'
              isDisabled={isAddUpiDisabled || isPaymentDisabled}
              caption={getAddUpiCaption()}
            />
          </div>
        </div>
      </div>
    </BottomSheetContent>
  )
}

const UnCheckedIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <circle cx="10" cy="10" r="9" stroke="#D1D6D6" stroke-width="2"/>
</svg>`

const CheckedIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17317C0.00433284 8.00043 -0.193701 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C19.9968 7.34881 18.9422 4.80712 17.0676 2.93244C15.1929 1.05776 12.6512 0.00317572 10 0V0ZM14.884 8.384L9.384 13.884C9.26793 14.0001 9.13011 14.0922 8.97844 14.1551C8.82676 14.2179 8.66418 14.2502 8.5 14.2502C8.33582 14.2502 8.17325 14.2179 8.02157 14.1551C7.8699 14.0922 7.73208 14.0001 7.616 13.884L5.116 11.384C4.88155 11.1495 4.74984 10.8316 4.74984 10.5C4.74984 10.1684 4.88155 9.85045 5.116 9.616C5.35046 9.38155 5.66844 9.24984 6 9.24984C6.33157 9.24984 6.64955 9.38155 6.884 9.616L8.5 11.232L13.116 6.616C13.2321 6.49991 13.3699 6.40782 13.5216 6.345C13.6733 6.28217 13.8358 6.24984 14 6.24984C14.1642 6.24984 14.3267 6.28217 14.4784 6.345C14.6301 6.40782 14.7679 6.49991 14.884 6.616C15.0001 6.73209 15.0922 6.86991 15.155 7.02158C15.2178 7.17326 15.2502 7.33583 15.2502 7.5C15.2502 7.66417 15.2178 7.82674 15.155 7.97842C15.0922 8.13009 15.0001 8.26791 14.884 8.384Z" fill="#232323"/>
</svg>`
